import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /**
   * F100 (С-I, круг 6, UX): выключенная кнопка остаётся в порядке Tab — `aria-disabled` вместо
   * нативного `disabled`, нажатие отсекается. Чтец на фокусе произносит причину из
   * `aria-describedby` (подвал диалога, § 4.1); с нативным `disabled` до кнопки не дойти.
   */
  focusableWhenDisabled?: boolean;
  /**
   * F100 (С-I, круг 7, UX): нажатие на выключенную фокусируемую кнопку — не отправка, а путь
   * обратно к причине (например, фокус в первое незаполненное обязательное поле).
   */
  onDisabledClick?: () => void;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    disabled,
    children,
    leadingIcon,
    trailingIcon,
    className,
    type = 'button',
    focusableWhenDisabled,
    onDisabledClick,
    onClick,
    'aria-disabled': ariaDisabled,
    ...rest
  },
  ref,
) {
  const inert = Boolean(disabled || loading);
  // Во время отправки кнопка выключена по-настоящему: повторное нажатие не должно доходить.
  const soft = inert && focusableWhenDisabled === true && !loading;
  const classes = [
    styles.button,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    loading ? styles.loading : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      // F100 (С-I): вариант виден контейнерам (подвал `Dialog` набирает Ghost по-своему).
      data-variant={variant}
      disabled={!soft && inert}
      aria-disabled={soft ? true : ariaDisabled}
      aria-busy={loading || undefined}
      onClick={
        soft
          ? (e) => {
              e.preventDefault();
              onDisabledClick?.();
            }
          : onClick
      }
      {...rest}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {!loading && leadingIcon && <span className={styles.icon}>{leadingIcon}</span>}
      <span className={styles.label}>{children}</span>
      {!loading && trailingIcon && <span className={styles.icon}>{trailingIcon}</span>}
    </button>
  );
});
